import { useMemo, useState } from 'react';
import Carousel from 'react-material-ui-carousel';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import WhiteOverlay from 'components/commercetools-ui/atoms/overlay/white-overlay';
import usePreloadImages from 'helpers/hooks/usePreloadImages';
import { StaticImageData } from 'next/image';
import { Reference } from 'types/reference';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet } from 'helpers/utils/screensizes';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';

export interface HeroProps {
  data: any;
}

interface Slide {
  ctas: any;
  banner_desktop_image: { url: string | StaticImageData | undefined; title: string | undefined; description: string | undefined };
  banner_heading: string | undefined;
  banner_description: string | undefined;
  call_to_action: { title: string | undefined; href: string | Reference | undefined };
}

// Define the type for the custom indicators props
interface CustomIndicatorsProps {
  index: number;
  length: number;
  onIndicatorClick: (i: number) => void;
  data: any;
}

const CustomIndicators: React.FC<CustomIndicatorsProps> = ({ index, length, onIndicatorClick, data }) => {
  const width = useWindowWidth();

  return (
    <div className={` ${data?.banner_slider?.length > 1 ? 'custom-indicator-container' : 'hidden'}`}>
      {Array.from({ length }).map((_, i) => (
        <button
          key={i}
          className={`custom-indicator ${index === i ? 'custom-indicator-active' : ''}`}
          onClick={() => onIndicatorClick(i)} // Call the click handler
          aria-label={`Slide ${i + 1}`} // Accessibility feature
        >
          {/* Place your custom SVG icon here */}
          <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15" height="15" viewBox="0 0 48 48">
            <path d="M36.5,42h-25C8.467,42,6,39.532,6,36.5v-25C6,8.468,8.467,6,11.5,6h25c3.033,0,5.5,2.468,5.5,5.5v25C42,39.532,39.533,42,36.5,42z"></path>
          </svg>
        </button>
      ))}
    </div>
  );
};

const Hero: React.FC<HeroProps> = ({ data }) => {
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTabletSize] = useMediaQuery(tablet);

  // Preload images
  usePreloadImages([
    data?.banner_slider?.[0]?.banner_desktop_image?.url,
    data?.banner_slider?.[1]?.banner_desktop_image?.url,
  ]);

  // Function to handle slide change via custom indicators
  const handleIndicatorClick = (index: number) => {
    setCurrentIndex(index);
  };

  const cmsCssClass = data?.banner_slider?.find((css: { additional_css: any }) => css.additional_css)?.additional_css;

  return (
    <>
      {loading && <WhiteOverlay />}
      <div className={`hero-slider-container relative w-full ${cmsCssClass}`}>
        <Carousel
          autoPlay={true}
          indicators={false}
          navButtonsAlwaysVisible={isTabletSize && data?.banner_slider?.length > 1}
          navButtonsAlwaysInvisible={true}
          onChange={(index) => setCurrentIndex(index !== undefined ? index : 0)}
          navButtonsProps={{
            className: 'nav-button',
          }}
          duration={8000}
          interval={8000}
        >
          {data?.banner_slider?.map((slide: Slide, index: number) => (
            <div key={index} className={`slide ${currentIndex === index ? 'is-active' : ''}`}>
              <div className=" relative hero-slider-sm hero-slider-md hero-slider-lg hero-slider-2xl">
                <picture>
                  <source media="(max-width:699px)" srcSet={`${slide?.banner_desktop_image?.url}`} />
                  <source media="(max-width:640px)" srcSet={`${slide?.banner_desktop_image?.url}`} />
                  <Image
                    priority
                    loading="eager"
                    src={slide?.banner_desktop_image?.url}
                    alt={slide?.banner_desktop_image?.description}
                    fill
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'bottom',
                      maxHeight: '350px',
                    }}
                    className=""
                    onLoad={() => setLoading(false)}
                    height={300}
                    width={500}
                    sizes="(max-width: 468px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </picture>
              </div>
              <div className="slide-content absolute top-[30%] w-full text-center">
                <div>
                  <Typography
                    as="h1"
                    className={`fontSize34-sm font-semibold lineHeight-xl fontSize48-xl lineHeight-2xl fontSize62-2xl text-center text-white shadow-sm`}
                  >
                    {slide?.banner_heading}
                  </Typography>
                </div>
                <div className="mt-[20px] w-full xl:mt-[25px] px-[20px] sm:px-[30px] md:px-[40px]">
                  <Typography className="fontSize16-xl font-medium font-size16-sm fontSize20-2xl text-center  tracking-wide text-white ">
                    {slide?.banner_description}
                  </Typography>
                </div>
              </div>
            </div>
          ))}
        </Carousel>
        <CustomIndicators
          index={currentIndex}
          length={data?.banner_slider?.length}
          onIndicatorClick={handleIndicatorClick}
          data={data}
        />
      </div>
    </>
  );
};

export default Hero;
